import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Position } from '../types';
import { DOMService } from '../../services/dom.service';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'wfm-tooltip',
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.scss']
})
export class TooltipComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() content: TemplateRef<any> | string;

  private $tooltipData: any;
  @Input()
  set tooltipData(data: any) {
    this.$tooltipData = data;
    this.window.setTimeout(() => this.ngAfterViewInit(), 0);
  }

  get tooltipData(): any {
    return this.$tooltipData;
  }

  @Input() position: Position = Position.bottom;

  @Input() width = '';

  @Input() maxWidth = '';

  @Input() target: HTMLElement;

  @ViewChild('defaultTemplate') defaultTemplate: TemplateRef<any>;
  @ViewChild('container') container: ElementRef<any>;
  @ViewChild('tooltip') tooltip: ElementRef<any>;

  private window: Window;

  constructor(private domService: DOMService, private element: ElementRef, @Inject(DOCUMENT) private document: any) {
    (element.nativeElement as HTMLElement).hidden = true;
    this.window = (this.document as Document).defaultView;
  }

  ngOnDestroy(): void {
    (this.element.nativeElement as HTMLElement).hidden = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes &&
      ((changes.target && !changes.target.firstChange && changes.target.currentValue) || changes.tooltipData)
    ) {
      this.window.setTimeout(() => this.ngAfterViewInit(), 0);
    }
  }

  ngAfterViewInit(): void {
    if (!this.target) return;

    let overflow = 0;
    (this.element.nativeElement as HTMLElement).hidden = false;

    const container: HTMLElement = this.container.nativeElement;
    const tooltip: HTMLElement = this.tooltip.nativeElement;
    let position = this.position;
    let trys = 0;

    do {
      this.setPositionStyle(container, position);
      this.setTooltipStyle(tooltip, position);

      const over = this.domService.identifyWindowOverflow(tooltip);

      if (over) {
        overflow = overflow | over;

        // if overflow on all sides, use original position and accept overflow
        if (overflow === parseInt('1111', 2)) {
          this.setPositionStyle(container, this.position);
          this.setTooltipStyle(tooltip, this.position);
          overflow = 0;
        }

        if (this.positions.top.indexOf(overflow) >= 0) {
          position = Position.top;
        }
        if (this.positions.bottom.indexOf(overflow) >= 0) {
          position = Position.bottom;
        }
        if (this.positions.left.indexOf(overflow) >= 0) {
          position = Position.left;
        }
        if (this.positions.right.indexOf(overflow) >= 0) {
          position = Position.right;
        }
      } else {
        overflow = over;
      }

      trys++;
    } while (overflow && trys < 4);
    // identify overflow and adjust position;
  }

  private setPositionStyle(content: HTMLElement, position: Position) {
    const targetRect = this.domService.getRect(this.target);
    const zIndex = this.domService.getEffectiveZIndex(this.target);

    content.style.bottom = content.style.right = '';
    content.style.top = targetRect.top + 'px';
    content.style.left = targetRect.left + 'px';

    if (zIndex) {
      content.style.zIndex = `${zIndex + 1}`;
    }

    if (position === Position.bottom) {
      content.style.top = targetRect.top + targetRect.height + 'px';
    }
    if (position === Position.right) {
      content.style.left = targetRect.left + targetRect.width + 'px';
    }
  }

  private setTooltipStyle(tooltip: HTMLElement, position: Position): any {
    tooltip.style.width = this.width;
    tooltip.style.maxWidth = this.maxWidth;

    tooltip.style.left = '';
    tooltip.style.top = '';
    tooltip.style['margin-top'] = '';
    tooltip.style['margin-bottom'] = '';
    tooltip.style['margin-left'] = '';
    tooltip.style['margin-right'] = '';

    for (const key of Object.keys(Position)) {
      tooltip.classList.remove(`tooltip--${key}`);
    }

    tooltip.classList.add(`tooltip--${position}`);

    if (position === Position.top) {
      tooltip.style.left = `${this.target.offsetWidth / 2}px`;
      tooltip.style['margin-bottom'] = '6px';
    }

    if (position === Position.bottom) {
      tooltip.style.left = `${this.target.offsetWidth / 2}px`;
      tooltip.style['margin-top'] = '6px';
    }

    if (position === Position.left) {
      tooltip.style.top = `${this.target.offsetHeight / 2}px`;
      tooltip.style['margin-right'] = '6px';
    }

    if (position === Position.right) {
      tooltip.style.top = `${this.target.offsetHeight / 2}px`;
      tooltip.style['margin-left'] = '6px';
    }
  }

  private positions = {
    top: [parseInt('0011', 2), parseInt('0100', 2), parseInt('0111', 2)],
    bottom: [parseInt('1000', 2), parseInt('1011', 2)],
    left: [parseInt('0001', 2), parseInt('0101', 2), parseInt('1001', 2), parseInt('1100', 2), parseInt('1101', 2)],
    right: [parseInt('0010', 2), parseInt('0110', 2), parseInt('1010', 2), parseInt('1110', 2)]
  };
}
